import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quizApi } from "@/lib/api";
import Loading from "@/components/app/Loading";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { ArrowLeft, ArrowRight } from "lucide-react";

const LIKERT = [
  { value: 1, label: "Strongly Disagree" },
  { value: 2, label: "Disagree" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Agree" },
  { value: 5, label: "Strongly Agree" },
];

export default function Quiz() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    quizApi.getQuestions()
      .then((d) => setQuestions(d.questions || d || []))
      .catch((e) => toast.error(e.message || "Failed to load questions"));
  }, []);

  const total = questions?.length || 0;
  const q = questions?.[idx];
  const progress = total ? Math.round(((idx + 1) / total) * 100) : 0;
  const allAnswered = useMemo(
    () => questions && questions.every((qq) => answers[qq.id] != null),
    [questions, answers]
  );

  if (!questions) return <Loading label="Loading questions..." />;

  const setValue = (v) => {
    setAnswers((a) => ({ ...a, [q.id]: v }));
  };

  const goNext = () => {
    if (idx < total - 1) setIdx(idx + 1);
  };
  const goPrev = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const submit = async () => {
    if (!allAnswered) {
      toast.error("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await quizApi.submit(answers);
      await refresh();
      navigate("/insights", { replace: true });
      toast.success("Your archetype profile is ready.");
    } catch (e) {
      toast.error(e.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2 text-xs" style={{ color: "var(--pa-text-muted)" }}>
          <span data-testid={IDS.quizProgress}>Question {idx + 1} of {total}</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full h-1 rounded-full" style={{ background: "var(--pa-card-border)" }}>
          <div
            className="h-1 rounded-full transition-all"
            style={{ width: `${progress}%`, background: "var(--pa-gold)" }}
          />
        </div>
      </div>

      <div className="pa-card p-5 md:p-8 mb-6">
        <p className="pa-serif text-lg md:text-xl leading-relaxed" style={{ color: "var(--pa-text-primary)" }}>
          {q?.text || q?.prompt || q?.question}
        </p>
      </div>

      <div className="space-y-2 mb-8">
        {LIKERT.map((opt) => {
          const active = answers[q?.id] === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setValue(opt.value)}
              className="w-full text-left transition-all"
              data-testid={`quiz-option-${opt.value}`}
              style={{
                background: active ? "rgba(201,162,39,0.12)" : "var(--pa-card)",
                border: `1px solid ${active ? "var(--pa-gold)" : "var(--pa-card-border)"}`,
                borderRadius: 8,
                padding: "14px 16px",
                color: active ? "var(--pa-gold-light)" : "var(--pa-text-primary)",
                minHeight: 52,
              }}
            >
              <span className="flex items-center justify-between">
                <span className="text-sm md:text-base">{opt.label}</span>
                <span
                  className="w-5 h-5 rounded-full inline-block"
                  style={{
                    border: `2px solid ${active ? "var(--pa-gold)" : "var(--pa-card-border)"}`,
                    background: active ? "var(--pa-gold)" : "transparent",
                  }}
                />
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          className="pa-btn-ghost inline-flex items-center gap-2 disabled:opacity-30"
          onClick={goPrev}
          disabled={idx === 0}
        >
          <ArrowLeft size={16} /> Back
        </button>
        {idx < total - 1 ? (
          <button
            className="pa-btn-gold inline-flex items-center gap-2"
            onClick={goNext}
            disabled={answers[q.id] == null}
            data-testid={IDS.quizNextBtn}
          >
            Next <ArrowRight size={16} />
          </button>
        ) : (
          <button
            className="pa-btn-gold inline-flex items-center gap-2"
            onClick={submit}
            disabled={!allAnswered || submitting}
            data-testid={IDS.quizSubmitBtn}
          >
            {submitting ? "Submitting..." : "See my archetypes"}
          </button>
        )}
      </div>
    </div>
  );
}
